import { useSelector } from 'react-redux';

import { Store } from 'types/store';
import { isMemberAdd, isVehicleAdd } from '../utils/amendmentUtils';
import { ResolvedAddOnPrice, resolveAddOnPrice } from '../utils/pricingUtils';

export type AddOnPricing = ResolvedAddOnPrice & {
  addedCount: number;
  isAddAllowed: boolean;
};

/**
 * Everything the customise step needs to price and gate the two things it can
 * stage against a renewal quote. Vehicles and members resolve off the same price
 * ladder and the same "amendments" array, so both are returned from one hook -
 * every call site needs both groups and would otherwise subscribe twice.
 */
const useAmendmentPricing = () => {
  const { amendments, pageModel, monthlyFlex } = useSelector((state: Store) => state.expiryRenewal);

  const isMonthlyPolicy = monthlyFlex || pageModel?.isMonthlyLongerTermPolicy;
  const isDiscounted = pageModel?.discountModel?.isDiscountedPrice;
  const priceContext = { isMonthlyPolicy, isDiscounted };

  // A policy paid upfront with a cover period other than 12 months isn't billed per year, so the /year suffix would be misleading here.
  const isNotAnnual = !isMonthlyPolicy && pageModel?.coverPeriod !== undefined && pageModel.coverPeriod !== 12;
  const yearOrBlankLabel = isNotAnnual ? '' : '/year';
  const periodLabel = isMonthlyPolicy ? '/month' : yearOrBlankLabel;

  // Both guards narrow on productCode as well as amendmentType - "amendments" is
  // shared, so counting on the type alone prices a member as an extra vehicle.
  const addedVehicles = amendments.filter(isVehicleAdd).length;
  const addedMembers = amendments.filter(isMemberAdd).length;

  // maxNumberOf*Adds is remaining headroom, so it counts down as we stage adds.
  const vehicle: AddOnPricing = {
    addedCount: addedVehicles,
    isAddAllowed: !!pageModel?.isVehicleAddAllowed
      && addedVehicles < (pageModel?.extraVehicleDetails?.maxNumberOfVehicleAdds ?? 0),
    ...resolveAddOnPrice(pageModel?.extraVehicleDetails, priceContext),
  };

  const member: AddOnPricing = {
    addedCount: addedMembers,
    isAddAllowed: !!pageModel?.isMemberAddAllowed
      && addedMembers < (pageModel?.extraPersonDetails?.maxNumberOfPeopleAdds ?? 0),
    ...resolveAddOnPrice(pageModel?.extraPersonDetails, priceContext),
  };

  return { isMonthlyPolicy, periodLabel, vehicle, member };
};

export default useAmendmentPricing;
